import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calculator, RefreshCw, Info, RotateCcw } from "lucide-react";
import { SplitLocationInput } from "@/pages/ProductMaster";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const StockTab = ({
  formData,
  handleChange,
  isNew,
  calculationPreview,
  calculating,
  handleCalculatePreview,
  existingBatches,
  initialExpiryBatch,
  setInitialExpiryBatch,
  creatingBatch,
  handleCreateInitialBatch,
}) => {
  const [recalculating, setRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);

  const handleRecalculate = async () => {
    if (!formData.id) return;
    setRecalculating(true);
    setRecalcResult(null);
    try {
      // Send the value that's currently sitting in the input field so the
      // backend compares the movement-history stock against what the user
      // sees (not the DB row, which may not match the unsaved form edits).
      const formStock = Number(formData.current_stock) || 0;
      const res = await axios.get(
        `${API}/products/${formData.id}/recalculate-stock`,
        { params: { form_stock: formStock } },
      );
      setRecalcResult(res.data);
      if (res.data.needs_correction) {
        toast.info(`Stock should be ${res.data.calculated_stock} (currently ${res.data.current_stock_on_system}). Difference: ${res.data.difference}`);
      } else {
        toast.success("Stock is correct!");
      }
    } catch {
      toast.error("Failed to recalculate");
    } finally {
      setRecalculating(false);
    }
  };

  const applyRecalculation = async () => {
    if (!formData.id) return;
    setRecalculating(true);
    try {
      const formStock = Number(formData.current_stock) || 0;
      const res = await axios.get(
        `${API}/products/${formData.id}/recalculate-stock`,
        { params: { apply: true, form_stock: formStock } },
      );
      if (res.data.applied) {
        toast.success(res.data.message);
        handleChange("current_stock", res.data.calculated_stock);
        setRecalcResult(null);
      }
    } catch {
      toast.error("Failed to apply correction");
    } finally {
      setRecalculating(false);
    }
  };

  const totalStockValue = ((formData.average_cost_price_excl_vat || formData.last_cost_price_excl_vat || 0) * formData.current_stock).toFixed(2);

  return (
    <div className="space-y-4">
      {/* Current Stock Info */}
      <div className="grid grid-cols-4 gap-4">
        <div>
          <Label>Current Stock</Label>
          <div className="flex gap-1">
            <Input
              type="number"
              step={formData.sold_per_kg ? "0.001" : "1"}
              value={formData.current_stock || ""}
              onChange={(e) => handleChange("current_stock", e.target.value === "" ? 0 : parseFloat(e.target.value))}
            />
            {!isNew && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 h-10 px-2"
                onClick={handleRecalculate}
                disabled={recalculating}
                title="Recalculate stock from movement history"
                data-testid="recalculate-stock-btn"
              >
                <RotateCcw className={`h-4 w-4 ${recalculating ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
        </div>
        <div>
          <Label>Stock on Order</Label>
          <Input
            type="number"
            value={formData.stock_on_order || 0}
            disabled
            className="bg-gray-100"
            title="Calculated from pending Purchase Orders"
          />
        </div>
        <div>
          <Label>Total Stock Value</Label>
          <div className="h-10 flex items-center px-3 bg-gray-100 rounded border text-sm font-semibold">
            R {totalStockValue}
          </div>
        </div>
        <div>
          <Label>VAT Category</Label>
          <Select
            value={formData.vat_category || "Standard 15%"}
            onValueChange={(value) => handleChange("vat_category", value)}
          >
            <SelectTrigger data-testid="vat-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Standard 15%">Standard 15%</SelectItem>
              <SelectItem value="Zero-rated 0%">Zero-rated 0%</SelectItem>
              <SelectItem value="VAT Exempt Eligible">VAT Exempt Eligible</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Recalculation Result */}
      {recalcResult && recalcResult.needs_correction && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-amber-800">Stock Discrepancy Found</p>
              <p className="text-sm text-amber-700">
                System shows <strong>{recalcResult.current_stock_on_system}</strong> but movement history says it should be <strong>{recalcResult.calculated_stock}</strong> (difference: {recalcResult.difference > 0 ? '+' : ''}{recalcResult.difference})
              </p>
              <p className="text-xs text-amber-600 mt-1">
                GRV in: +{recalcResult.summary.total_grv_in} | Sold: -{recalcResult.summary.total_sold} | Refunds: +{recalcResult.summary.total_refunds} {recalcResult.summary.total_case_sold > 0 ? `| Case sales: -${recalcResult.summary.total_case_sold}` : ''}
              </p>
            </div>
            <Button
              onClick={applyRecalculation}
              disabled={recalculating}
              className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
              data-testid="apply-recalc-btn"
            >
              Fix to {recalcResult.calculated_stock}
            </Button>
          </div>
        </div>
      )}

      {/* Stock Levels */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <div className="flex justify-between items-center mb-3">
          <Label className="text-base font-semibold">Stock Levels</Label>
          {!isNew && (
            <div className="flex items-center gap-2">
              <div className="flex items-center space-x-2 mr-4">
                <Checkbox
                  id="auto_calculate"
                  checked={formData.auto_calculate_stock_levels || false}
                  onCheckedChange={(checked) => handleChange("auto_calculate_stock_levels", checked)}
                />
                <Label htmlFor="auto_calculate" className="text-sm font-normal">
                  Auto-calculate from sales
                </Label>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCalculatePreview}
                disabled={calculating}
                className="text-purple-600 border-purple-300 hover:bg-purple-50"
              >
                {calculating ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Calculator className="h-4 w-4 mr-1" />}
                Calculate
              </Button>
            </div>
          )}
        </div>

        {calculationPreview && (
          <div className={`mb-4 p-3 rounded-lg border ${calculationPreview.has_sales_data ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-300'}`}>
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div className="text-sm flex-1">
                {calculationPreview.has_sales_data ? (
                  <>
                    <p className="font-medium text-green-700">Calculation based on 6-month sales data</p>
                    <p className="text-green-600 mb-2">
                      Total sold: {calculationPreview.total_6_month_sales} units | 
                      Weekly avg: {calculationPreview.calculated_weekly_sales?.toFixed(2)} units
                    </p>
                    {/* Monthly breakdown table */}
                    {calculationPreview.monthly_breakdown && calculationPreview.monthly_breakdown.length > 0 && (
                      <div className="mt-2 bg-white rounded border border-green-200 overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-green-100">
                            <tr>
                              <th className="px-2 py-1 text-left font-medium text-green-800">Month</th>
                              <th className="px-2 py-1 text-right font-medium text-green-800">Qty Sold</th>
                              <th className="px-2 py-1 text-left font-medium text-green-800">Source</th>
                            </tr>
                          </thead>
                          <tbody>
                            {calculationPreview.monthly_breakdown.map((m, idx) => (
                              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-green-50'}>
                                <td className="px-2 py-1 text-green-700">{m.period_label}</td>
                                <td className="px-2 py-1 text-right font-medium text-green-800">{m.qty_sold}</td>
                                <td className="px-2 py-1 text-green-600 text-xs">{m.data_source === 'imported' ? '📊 Imported' : m.data_source === 'live' ? '💳 Live' : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-yellow-700">Not enough sales data for calculation. Using current values.</p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="text-sm">Minimum Level</Label>
            <Input
              type="number"
              value={formData.minimum_level || ""}
              onChange={(e) => handleChange("minimum_level", e.target.value === "" ? 0 : parseInt(e.target.value))}
            />
          </div>
          <div>
            <Label className="text-sm">Reorder Level</Label>
            <Input
              type="number"
              value={formData.reorder_level || ""}
              onChange={(e) => handleChange("reorder_level", e.target.value === "" ? 0 : parseInt(e.target.value))}
            />
          </div>
          <div>
            <Label className="text-sm">Maximum Level</Label>
            <Input
              type="number"
              value={formData.maximum_level || ""}
              onChange={(e) => handleChange("maximum_level", e.target.value === "" ? 0 : parseInt(e.target.value))}
            />
          </div>
        </div>

        <SubstituteProductPicker
          formData={formData}
          handleChange={handleChange}
        />
      </div>

      {/* Location */}
      <div className="border rounded-lg p-4">
        <Label className="text-base font-semibold mb-3 block">Location</Label>
        <SplitLocationInput
          shelfValue={formData.shelf_number || ""}
          binValue={formData.bin_number || ""}
          onShelfChange={(value) => {
            handleChange("shelf_number", value);
            // Keep canonical `location` field in sync so downstream reports
            // (Print Stocktake "Per Shelf", get_shelf_bins) don't keep showing
            // the product under its old shelf code.
            const bin = formData.bin_number || "";
            const padded = bin && /^\d+$/.test(bin) ? bin.padStart(4, "0") : bin;
            const loc = value && padded ? `${value.toUpperCase()}/${padded}` : (value || "").toUpperCase();
            handleChange("location", loc);
          }}
          onBinChange={(value) => {
            handleChange("bin_number", value);
            const padded = value && /^\d+$/.test(value) ? value.padStart(4, "0") : value;
            const shelf = (formData.shelf_number || "").toUpperCase();
            const loc = shelf && padded ? `${shelf}/${padded}` : shelf;
            handleChange("location", loc);
          }}
          onSave={() => {}}
        />
        <p className="text-xs text-gray-500 mt-2">
          Format: SHELF/BIN (e.g., B1-1/0001, CHIC/0250)
        </p>
      </div>

      {/* Track Expiry */}
      <div className={`border rounded-lg p-4 ${formData.track_expiry ? 'bg-red-50 border-red-300' : 'bg-gray-50'}`}>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="track_expiry"
            checked={formData.track_expiry || false}
            onCheckedChange={(checked) => handleChange("track_expiry", checked)}
            data-testid="track-expiry-checkbox"
          />
          <Label htmlFor="track_expiry" className={`font-medium ${formData.track_expiry ? 'text-red-700' : 'text-gray-700'}`}>
            Track Expiry Dates
          </Label>
        </div>
        <p className="text-xs text-gray-500 mt-2 ml-6">
          Enable expiry date tracking for this product. You will be prompted to enter expiry dates when receiving stock.
        </p>
        
        {formData.track_expiry && (
          <div className="mt-3 ml-6 space-y-4">
            <div>
              <Label className="text-sm">Warning Period</Label>
              <Select 
                value={String(formData.expiry_warning_days || 30)} 
                onValueChange={(value) => handleChange("expiry_warning_days", parseInt(value))}
              >
                <SelectTrigger className="w-48 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 Days</SelectItem>
                  <SelectItem value="14">14 Days</SelectItem>
                  <SelectItem value="30">1 Month (30 days)</SelectItem>
                  <SelectItem value="120">4 Months (120 days)</SelectItem>
                  <SelectItem value="365">1 Year (365 days)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Alert when product expires within this period
              </p>
            </div>
            
            {existingBatches.length > 0 && (
              <div className="bg-white border border-red-200 rounded-lg p-3">
                <Label className="text-sm font-semibold text-red-700">Existing Expiry Batches ({existingBatches.length})</Label>
                <div className="mt-2 space-y-2 max-h-32 overflow-y-auto">
                  {existingBatches.map((batch, idx) => (
                    <div key={batch.id || idx} className="flex justify-between text-sm bg-red-50 p-2 rounded">
                      <span>
                        {batch.batch_number ? `${batch.batch_number} - ` : ""}
                        Exp: {new Date(batch.expiry_date).toLocaleDateString("en-ZA")}
                      </span>
                      <span className="font-medium">{batch.quantity_remaining} remaining</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {formData.current_stock > 0 && (
              <div className="bg-white border border-orange-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold text-orange-700">
                    Add Expiry for Current Stock
                  </Label>
                  {!initialExpiryBatch.enabled && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-orange-400 text-orange-700 hover:bg-orange-50"
                      onClick={() => setInitialExpiryBatch(prev => ({ 
                        ...prev, 
                        enabled: true,
                        quantity: String(formData.current_stock)
                      }))}
                      data-testid="add-initial-expiry-btn"
                    >
                      + Add Expiry Batch
                    </Button>
                  )}
                </div>
                
                {initialExpiryBatch.enabled && (
                  <div className="space-y-3 mt-3 pt-3 border-t border-orange-200">
                    <p className="text-xs text-orange-600">
                      Create an expiry batch for current stock (Qty: {formData.current_stock})
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Expiry Date *</Label>
                        <Input
                          type="date"
                          value={initialExpiryBatch.expiry_date}
                          onChange={(e) => setInitialExpiryBatch(prev => ({ ...prev, expiry_date: e.target.value }))}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Quantity *</Label>
                        <Input
                          type="number"
                          value={initialExpiryBatch.quantity}
                          onChange={(e) => setInitialExpiryBatch(prev => ({ ...prev, quantity: e.target.value }))}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Batch # (optional)</Label>
                        <Input
                          value={initialExpiryBatch.batch_number}
                          onChange={(e) => setInitialExpiryBatch(prev => ({ ...prev, batch_number: e.target.value }))}
                          className="text-sm"
                          placeholder="LOT123"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleCreateInitialBatch}
                        disabled={creatingBatch}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        {creatingBatch ? "Creating..." : "Create Batch"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setInitialExpiryBatch({ enabled: false, expiry_date: "", quantity: "", batch_number: "" })}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Compact picker for "Substitute / equivalent product" used by Auto-Suggest
 * reorder. When this product would normally trigger an auto-reorder but the
 * substitute is in healthy stock, the system hides this product from the
 * suggested-PO list. Symmetric: set the same substitute on both products to
 * make the protection two-way.
 */
const SubstituteProductPicker = ({ formData, handleChange }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);

  // Hydrate the chip when an existing product is loaded with a substitute.
  useEffect(() => {
    const id = formData.substitute_product_id;
    if (!id) {
      setSelectedDetail(null);
      return;
    }
    if (selectedDetail && selectedDetail.id === id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/products/${id}`);
        if (!cancelled) setSelectedDetail(res.data || null);
      } catch {
        if (!cancelled) setSelectedDetail({ id, name: "(not found)", plu_code: "" });
      }
    })();
    return () => { cancelled = true; };
  }, [formData.substitute_product_id, selectedDetail]);

  // Debounced live search.
  useEffect(() => {
    if (!showResults) return;
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/products/search?q=${encodeURIComponent(query.trim())}`);
        setResults((res.data || []).filter((p) => p.id !== formData.id).slice(0, 10));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, showResults, formData.id]);

  const pick = (p) => {
    handleChange("substitute_product_id", p.id);
    setSelectedDetail(p);
    setQuery("");
    setResults([]);
    setShowResults(false);
  };

  const clear = () => {
    handleChange("substitute_product_id", null);
    setSelectedDetail(null);
    setQuery("");
    setResults([]);
  };

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 mt-3" data-testid="substitute-product-picker">
      <Label className="text-sm font-semibold text-blue-900">Substitute / Equivalent Product</Label>
      <p className="text-xs text-blue-700/80 mb-2">
        Different brand, same purpose. When this product runs low but the substitute still has healthy stock, the auto-suggest reorder will skip it.
      </p>

      {selectedDetail ? (
        <div className="flex items-center justify-between rounded-md border border-blue-300 bg-white px-3 py-2">
          <div className="text-sm">
            <div className="font-medium text-gray-900">{selectedDetail.name || "(unnamed)"}</div>
            <div className="text-xs text-gray-500">
              PLU {selectedDetail.plu_code || "—"}
              {typeof selectedDetail.current_stock === "number" && (
                <span className="ml-2">· Stock: {selectedDetail.current_stock}</span>
              )}
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={clear}
            data-testid="substitute-clear-btn"
          >
            Remove
          </Button>
        </div>
      ) : (
        <div className="relative">
          <Input
            value={query}
            placeholder="Search by PLU, barcode or name…"
            onFocus={() => setShowResults(true)}
            onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
            data-testid="substitute-search-input"
          />
          {showResults && query.trim().length >= 2 && (
            <div className="absolute z-20 mt-1 w-full rounded-md border bg-white shadow-lg max-h-64 overflow-y-auto">
              {searching ? (
                <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>
              ) : results.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-500">No matches</div>
              ) : (
                results.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b last:border-b-0"
                    onClick={() => pick(p)}
                    data-testid={`substitute-pick-${p.id}`}
                  >
                    <div className="font-medium text-gray-900">{p.name}</div>
                    <div className="text-xs text-gray-500">
                      PLU {p.plu_code || "—"} · Stock: {p.current_stock ?? 0}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
